import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { brandColors, getGradeColor } from '@config/brandTokens';
import { generateFoundationCertificatePDF } from '@utils/generateCertificatesPDF';
import { foundationCertificateNo } from '@utils/contestCertification';

export interface FoundationSkillRow {
  skillName: string;
  grade?: string | null;
  score?: number | null;
  value?: string;
  recordedDate?: string | null;
  quarter?: string | null;
}

export interface FoundationBlock {
  sport: string;
  ageCategory: string;
  levelCode?: string | null;
  levelLabel?: string;
  scorePercent?: number | null;
  gradedCount?: number;
  skillCount?: number;
  updatedAt?: string | null;
  skills: FoundationSkillRow[];
  quarterTrend?: Array<{ key: string; label: string; score: number | null }>;
}

const FoundationScoutPanel: React.FC<{
  foundation: FoundationBlock | null;
  player: {
    firstName?: string;
    lastName?: string;
    playerIdCode?: string;
    id?: string;
  };
}> = ({ foundation, player }) => {
  const [open, setOpen] = useState(false);
  const displayName = [player.firstName, player.lastName].filter(Boolean).join(' ').trim() || 'Player';
  const chartData = useMemo(
    () =>
      (foundation?.quarterTrend || []).map((row) => ({
        label: row.label.replace(/20(\d{2})/, "'$1"),
        score: row.score,
      })),
    [foundation]
  );
  const hasChart = chartData.some((row) => row.score != null);

  if (!foundation) {
    return (
      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, p: 2, bgcolor: '#fff' }}>
        <Typography sx={{ fontWeight: 800, color: brandColors.navy }}>Technical Foundation</Typography>
        <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.75 }}>
          No submitted foundation scores yet. After self-evaluation, the certificate, skill table, and quarter graph appear here.
        </Typography>
      </Box>
    );
  }

  const colors = getGradeColor(foundation.levelCode || 'C');
  const certNo = foundationCertificateNo(
    {
      sport: foundation.sport,
      ageCategory: foundation.ageCategory,
      levelCode: (foundation.levelCode || 'C') as any,
      levelLabel: foundation.levelLabel || 'Technical Foundation',
      scorePercent: Number(foundation.scorePercent || 0),
      skillCount: foundation.skillCount || 0,
      gradedCount: foundation.gradedCount || 0,
      complete: true,
      eligible: true,
      skillGrades: [],
    },
    player.playerIdCode || player.id
  );

  const download = () => {
    void generateFoundationCertificatePDF({
      player,
      sport: foundation.sport,
      ageCategory: foundation.ageCategory,
      levelCode: foundation.levelCode || 'C',
      levelLabel: foundation.levelLabel || 'Technical Foundation',
      scorePercent: Number(foundation.scorePercent || 0),
      skillsAssessed: foundation.gradedCount || foundation.skills.length,
      certificateNo: certNo,
      issuedOn: foundation.updatedAt ? new Date(foundation.updatedAt).toLocaleDateString() : undefined,
    });
  };

  return (
    <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff', overflow: 'hidden' }}>
      <Box
        sx={{
          px: 1.75,
          py: 1.25,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          borderBottom: '1px solid #eef2f7',
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 900, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif' }}>
            Technical Foundation
          </Typography>
          <Typography variant="caption" sx={{ color: brandColors.slate, fontWeight: 700 }}>
            {foundation.sport} · {foundation.ageCategory} · certificate, table, and quarter graph
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<WorkspacePremiumIcon />}
            onClick={() => setOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 800, borderColor: brandColors.navy, color: brandColors.navy }}
          >
            View certificate
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={download}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: brandColors.navy }}
          >
            Download
          </Button>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr 240px' }, gap: 0 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') setOpen(true);
          }}
          sx={{
            p: 1.75,
            borderRight: { md: '1px solid #eef2f7' },
            cursor: 'pointer',
            background: 'linear-gradient(180deg, #eef4ff 0%, #ffffff 70%)',
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: brandColors.blue }}>
            CERTIFICATE
          </Typography>
          <Typography sx={{ fontWeight: 900, color: brandColors.navy, mt: 0.6, fontFamily: '"Sora", Arial, sans-serif' }}>
            {displayName}
          </Typography>
          <Chip
            size="small"
            label={foundation.levelCode || '—'}
            sx={{ mt: 1, fontWeight: 900, bgcolor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
          />
          <Typography sx={{ mt: 1, fontWeight: 800, color: brandColors.navy }}>
            {foundation.scorePercent != null ? `${Number(foundation.scorePercent).toFixed(1)} / 100` : 'Pending'}
          </Typography>
          <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block' }}>
            {foundation.levelLabel}
          </Typography>
        </Box>

        <Box sx={{ minWidth: 0, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>Skill</TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>Grade</TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 800, fontSize: 12 }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {foundation.skills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">No foundation skills recorded.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                foundation.skills.map((skill, index) => {
                  const gradeColors = getGradeColor(skill.grade || 'C');
                  return (
                    <TableRow key={`${skill.skillName}-${index}`} hover>
                      <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>{skill.skillName}</TableCell>
                      <TableCell>
                        {skill.grade ? (
                          <Chip
                            size="small"
                            label={skill.grade}
                            sx={{ fontWeight: 800, height: 22, bgcolor: gradeColors.bg, color: gradeColors.text }}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>
                        {skill.score != null ? Number(skill.score).toFixed(0) : skill.value || '—'}
                      </TableCell>
                      <TableCell sx={{ color: brandColors.slate, fontSize: 12 }}>
                        {skill.recordedDate ? new Date(skill.recordedDate).toLocaleDateString() : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        <Box sx={{ p: 1.25, borderLeft: { md: '1px solid #eef2f7' }, minHeight: 180 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: brandColors.slate, mb: 0.5 }}>
            Improvement over quarters
          </Typography>
          {hasChart ? (
            <ResponsiveContainer width="100%" height={150}>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fontWeight: 700 }} axisLine={false} tickLine={false} />
                <YAxis domain={[40, 100]} hide />
                <Tooltip formatter={(value: number) => [Number(value).toFixed(1), 'Score']} />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={brandColors.orange}
                  strokeWidth={2.4}
                  dot={{ r: 3.5, fill: brandColors.blue }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" sx={{ color: brandColors.slate, mt: 2 }}>
              Graph appears after scores land in more than one quarter.
            </Typography>
          )}
        </Box>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: brandColors.navy }}>Technical Foundation Certificate</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              border: `2px solid ${brandColors.blue}`,
              borderRadius: 2,
              p: { xs: 2, md: 3.5 },
              textAlign: 'center',
              background: 'linear-gradient(180deg, #f8fbff 0%, #ffffff 55%)',
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.2rem', md: '1.45rem' }, color: brandColors.navy }}>
              Technical Foundation Certificate
            </Typography>
            <Typography sx={{ color: brandColors.blue, fontWeight: 700, mt: 0.75 }}>
              {foundation.levelLabel}
            </Typography>
            <Typography sx={{ color: brandColors.slate, mt: 2 }}>This is to certify that</Typography>
            <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', color: brandColors.navy, mt: 0.75 }}>
              {displayName}
            </Typography>
            {player.playerIdCode ? (
              <Typography sx={{ color: brandColors.slate, fontSize: '0.85rem', mt: 0.5 }}>
                Player ID: {player.playerIdCode}
              </Typography>
            ) : null}
            <Typography sx={{ mt: 2, color: brandColors.navy }}>
              has achieved{' '}
              <Chip
                size="small"
                label={foundation.levelCode || '—'}
                sx={{ fontWeight: 900, mx: 0.4, bgcolor: colors.bg, color: colors.text, border: `1px solid ${colors.border}` }}
              />{' '}
              level in {foundation.sport} Technical Foundation ({foundation.ageCategory}).
            </Typography>
            <Typography sx={{ fontWeight: 800, mt: 2, color: brandColors.navy }}>
              Composite score: {foundation.scorePercent != null ? Number(foundation.scorePercent).toFixed(1) : '—'} / 100
            </Typography>
            <Typography sx={{ color: brandColors.slate, mt: 0.75 }}>
              Skills assessed: {foundation.gradedCount || foundation.skills.length}
              {foundation.skillCount ? ` / ${foundation.skillCount}` : ''}
            </Typography>
            <Typography sx={{ color: brandColors.slate, fontSize: '0.78rem', mt: 2 }}>
              Issued by EncycGames · {foundation.updatedAt ? new Date(foundation.updatedAt).toLocaleDateString() : new Date().toLocaleDateString()} · {certNo}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
          <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={download} sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.navy }}>
            Download PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FoundationScoutPanel;
